import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { KeyRound, UploadCloud, CheckCircle2, Loader2, Film, Music } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const CHUNK = 5 * 1024 * 1024;

function fmtDetail(d) {
  if (d == null) return "Something went wrong.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(", ");
  return String(d);
}

export default function StudioPage() {
  const [token, setToken] = useState(sessionStorage.getItem("cgd_studio_token") || "");
  const [code, setCode] = useState("");
  const [loginError, setLoginError] = useState("");
  const [kind, setKind] = useState("video");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  const [uploadPct, setUploadPct] = useState(null);
  const [qcPct, setQcPct] = useState(false);
  const [uploadMsg, setUploadMsg] = useState("");
  const [drafts, setDrafts] = useState([]);
  const fileRef = useRef(null);

  const authHeaders = { "X-Studio-Token": token };

  const loadDrafts = async (t = token) => {
    if (!t) return;
    try {
      const { data } = await axios.get(`${API}/api/studio/drafts`, { headers: { "X-Studio-Token": t } });
      setDrafts(data);
    } catch (e) {
      if (e.response?.status === 401) {
        setToken("");
        sessionStorage.removeItem("cgd_studio_token");
      }
    }
  };

  useEffect(() => {
    loadDrafts();
    const id = setInterval(() => loadDrafts(), 15000);
    return () => clearInterval(id);
  }, [token]);

  const login = async () => {
    setLoginError("");
    try {
      const { data } = await axios.post(`${API}/api/studio/login`, { code });
      sessionStorage.setItem("cgd_studio_token", data.token);
      setToken(data.token);
    } catch (e) {
      setLoginError(fmtDetail(e.response?.data?.detail) || e.message);
    }
  };

  const upload = async () => {
    setUploadMsg("");
    if (!file || !title.trim()) {
      setUploadMsg("Pick a file and give the episode a title.");
      return;
    }
    try {
      const { data: init } = await axios.post(
        `${API}/api/studio/upload/init`,
        { filename: file.name, kind, title, description, tags: kind === "video" ? ["Season 3"] : ["Season 3"] },
        { headers: authHeaders }
      );
      const total = Math.ceil(file.size / CHUNK);
      for (let i = 0; i < total; i++) {
        const blob = file.slice(i * CHUNK, (i + 1) * CHUNK);
        await axios.post(`${API}/api/studio/upload/${init.upload_id}/chunk`, blob, {
          headers: { ...authHeaders, "Content-Type": "application/octet-stream" },
        });
        setUploadPct(Math.round(((i + 1) / total) * 100));
      }
      await axios.post(`${API}/api/studio/upload/${init.upload_id}/complete`, {}, { headers: authHeaders });
      setUploadPct(100);
      setQcPct(true);
      setUploadMsg("Uploaded — the QC pipeline is running. This takes a while for video; drafts appear below when the master + report are ready.");
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
    } catch (e) {
      setUploadMsg(fmtDetail(e.response?.data?.detail) || e.message);
      setUploadPct(null);
    }
  };

  const approve = async (number) => {
    try {
      await axios.post(`${API}/api/studio/approve/${number}`, {}, { headers: authHeaders });
      setUploadMsg(`Episode ${number} is LIVE.`);
      loadDrafts();
    } catch (e) {
      setUploadMsg(fmtDetail(e.response?.data?.detail) || e.message);
    }
  };

  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink px-6">
        <div className="w-full max-w-sm rounded-3xl border border-white/10 bg-panel/70 p-8">
          <KeyRound className="h-8 w-8 text-cherry" />
          <h1 className="mt-4 text-2xl font-bold text-white">CGD Studio</h1>
          <p className="mt-1 text-sm text-white/50">QC + publishing desk. Morgan's code required.</p>
          <input
            data-testid="studio-code-input"
            type="password"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="Studio code"
            className="mt-5 w-full rounded-xl border border-white/15 bg-ink px-4 py-3 text-white outline-none focus:border-cherry"
          />
          {loginError && (
            <p data-testid="studio-login-error" className="mt-2 text-sm text-cherry">
              {loginError}
            </p>
          )}
          <button
            data-testid="studio-login-btn"
            onClick={login}
            className="mt-4 w-full rounded-xl bg-cherry py-3 font-mono text-sm font-bold uppercase tracking-widest text-ink transition-colors hover:bg-pink"
          >
            Enter the studio
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ink px-6 py-12">
      <div className="mx-auto max-w-3xl">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold text-white">CGD Studio</h1>
          <button
            onClick={() => {
              sessionStorage.removeItem("cgd_studio_token");
              setToken("");
            }}
            className="font-mono text-xs uppercase tracking-widest text-white/40 hover:text-cherry"
          >
            Log out
          </button>
        </div>

        <section className="mt-8 rounded-3xl border border-white/10 bg-panel/70 p-6 sm:p-8">
          <h2 className="flex items-center gap-2 font-mono text-xs font-bold uppercase tracking-[0.2em] text-cherry">
            <UploadCloud className="h-4 w-4" /> Upload episode for QC
          </h2>
          <div className="mt-5 grid gap-4">
            <input
              data-testid="studio-title-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Episode title — e.g. Dating Dead? | The Lowdown with Morgan Chanel"
              className="w-full rounded-xl border border-white/15 bg-ink px-4 py-3 text-white outline-none focus:border-cherry"
            />
            <textarea
              data-testid="studio-description-input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Episode description (optional — goes live with the episode)"
              rows={3}
              className="w-full rounded-xl border border-white/15 bg-ink px-4 py-3 text-white outline-none focus:border-cherry"
            />
            <div className="flex gap-3">
              {["video", "audio"].map((k) => (
                <button
                  key={k}
                  data-testid={`studio-kind-${k}`}
                  onClick={() => setKind(k)}
                  className={`flex items-center gap-2 rounded-full border px-4 py-2 font-mono text-xs font-bold uppercase tracking-wider transition-colors ${
                    kind === k ? "border-cherry bg-cherry/15 text-cherry" : "border-white/15 text-white/50"
                  }`}
                >
                  {k === "video" ? <Film className="h-4 w-4" /> : <Music className="h-4 w-4" />} {k}
                </button>
              ))}
            </div>
            <input
              data-testid="studio-file-input"
              ref={fileRef}
              type="file"
              accept={kind === "video" ? "video/*" : "audio/*"}
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="w-full rounded-xl border border-dashed border-white/20 bg-ink px-4 py-6 text-sm text-white/60"
            />
            <button
              data-testid="studio-upload-btn"
              onClick={upload}
              disabled={uploadPct != null && uploadPct < 100}
              className="w-full rounded-xl bg-cherry py-3 font-mono text-sm font-bold uppercase tracking-widest text-ink transition-colors hover:bg-pink disabled:opacity-50"
            >
              {uploadPct != null && uploadPct < 100 ? `Uploading ${uploadPct}%` : "Send to the QC pipeline"}
            </button>
            {uploadPct != null && (
              <div data-testid="upload-progress" className="h-1.5 overflow-hidden rounded-full bg-white/10">
                <div className="h-full bg-cherry transition-all" style={{ width: `${uploadPct}%` }} />
              </div>
            )}
            {uploadMsg && (
              <p data-testid="studio-upload-msg" className="text-sm text-white/70">
                {qcPct && <Loader2 className="mr-1 inline h-4 w-4 animate-spin text-cherry" />}
                {uploadMsg}
              </p>
            )}
          </div>
        </section>

        <section className="mt-10">
          <h2 className="font-mono text-xs font-bold uppercase tracking-[0.2em] text-white/40">
            Drafts waiting for your approval
          </h2>
          {!drafts.length && (
            <p className="mt-4 rounded-2xl border border-dashed border-white/15 p-8 text-center text-sm text-white/40">
              No drafts right now — upload an episode and the pipeline will park it here with its report.
            </p>
          )}
          <div className="mt-4 space-y-6">
            {drafts.map((d, di) => {
              let report = null;
              try {
                report = d.qc_report ? JSON.parse(d.qc_report) : null;
              } catch (e) {}
              return (
                <article
                  key={d.number}
                  data-testid={`studio-draft-card-${d.number}`}
                  className="rounded-3xl border border-cherry/40 bg-panel/70 p-6"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-cherry">
                        Episode {d.number} · {d.duration}
                      </p>
                      <h3 className="mt-1 text-lg font-bold text-white">{d.title}</h3>
                    </div>
                    <button
                      data-testid={`draft-approve-${d.number}`}
                      onClick={() => approve(d.number)}
                      className="flex shrink-0 items-center gap-2 rounded-full bg-cherry px-5 py-2.5 font-mono text-xs font-bold uppercase tracking-widest text-ink transition-colors hover:bg-pink"
                    >
                      <CheckCircle2 className="h-4 w-4" /> Approve &amp; publish
                    </button>
                  </div>
                  {d.strip_url && (
                    <img
                      data-testid={`draft-strip-${d.number}`}
                      src={d.strip_url}
                      alt="QC before/after strip"
                      className="mt-4 w-full rounded-xl"
                    />
                  )}
                  {report && (
                    <div data-testid={`draft-report-${d.number}`} className="mt-4 rounded-xl border border-white/10 bg-ink/60 p-4 font-mono text-[11px] leading-relaxed text-white/60">
                      <p>OUT: {report.output?.width}×{report.output?.height} @ {report.output?.fps} · {report.output?.duration_s}s · {report.output?.video_codec || report.output?.audio_codec}</p>
                      <p className="mt-1 text-cherry">LIGHT: luma {report.light?.mean_luma} → 118 · sat {report.light?.mean_saturation} → 66</p>
                      <ul className="mt-1 list-disc pl-4">
                        {(report.corrections || []).map((c, ci) => (
                          <li key={ci}>{c}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {(d.video_url || d.audio_url) && (
                    <div className="mt-4">
                      {d.video_url ? (
                        <video data-testid={`draft-player-${d.number}`} src={d.video_url} controls className="w-full rounded-xl" />
                      ) : (
                        <audio data-testid={`draft-player-${d.number}`} src={d.audio_url} controls className="w-full" />
                      )}
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
}
